import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { space, themeGet } from 'styled-system';

import { Text } from 'common/components/Text';
import type { SpaceProps } from 'common/theme/system-types';
import type { RenderingType } from '../enums';
import ConfigurationListItem from './ConfigurationListItem';
import { trackAlternativeVariantClick } from './tracking';
import type { ArticleVariationGroup, ArticleVariationGroupValues } from './types';

interface ConfigurationGroupProps extends Pick<ArticleVariationGroup, 'title' | 'values'> {
  renderingType: RenderingType;
  selectedURL: Nullable<string>;
  onSelect: (sku: Nullable<string>) => void;
  listLength: number;
  titlePrefix: string;
}

const UnstyledList = styled.ul<SpaceProps>`
  ${space};
`;

const List = (props: ComponentProps<typeof UnstyledList>) => (
  <UnstyledList m="sp_0" p="sp_0" {...props} />
);

const UnstyledTitle = styled(Text)`
  ${space};

  &:first-of-type {
    margin-top: ${themeGet('space.sp_0')};
  }
`;

const Title = (props: ComponentProps<typeof UnstyledTitle>) => (
  <UnstyledTitle
    as="h3"
    color="sys.neutral.text.strong"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_20"
    my="sp_16"
    {...props}
  />
);

const ConfigurationGroup = ({
  title,
  values,
  renderingType,
  selectedURL,
  onSelect,
  listLength,
  titlePrefix,
}: ConfigurationGroupProps) => {
  const getClickHandler = (item: ArticleVariationGroupValues) => (key: Nullable<string>) => {
    trackAlternativeVariantClick({
      listLength,
      listPosition: item.listIndex,
      title: `${titlePrefix} ${item.title}`,
    });
    onSelect(key);
  };

  return (
    <>
      {title && <Title>{title}</Title>}
      <List>
        {values.map(item => (
          <ConfigurationListItem
            key={item.title}
            groupValue={item}
            renderingType={renderingType}
            selectedValue={selectedURL}
            onClick={getClickHandler(item)}
          />
        ))}
      </List>
    </>
  );
};

export default ConfigurationGroup;
